import { useCartContext } from "@@/carts/multi-carts/cart.context";
import type { ProductForm, ProductFormId } from "@towni/common";
import { orderUtils } from "@towni/common";
import { useEffect, useRef } from "react";

const useRegisterProductFormInCart = (productForm: ProductForm) => {
    const formHasFields = productForm.fields.length > 0;
    const context = useCartContext(context => ({
        productIds: orderUtils.getProductIds(context.cart.orderItems),
        registerProductForm: context.registerProductForm,
        unregisterProductForm: context.unregisterProductForm,
        registerProductFormAnswers: context.registerProductFormAnswers,
    }));

    // Register form in cart context if there is one
    const productFormIdRef = useRef<ProductFormId>(productForm._id);
    const productFormRef = useRef<ProductForm>(productForm);
    const registerProductFormRef = useRef(context.registerProductForm);
    const unregisterProductFormRef = useRef(context.unregisterProductForm);
    const hasRegisteredFormRef = useRef<ProductFormId | undefined>(undefined);

    useEffect(() => {
        productFormIdRef.current = productForm._id;
        productFormRef.current = productForm;
    }, [productForm._id, productForm]);

    useEffect(() => {
        registerProductFormRef.current = context.registerProductForm;
        unregisterProductFormRef.current = context.unregisterProductForm;
    }, [context.registerProductForm, context.unregisterProductForm]);

    useEffect(() => {
        if (formHasFields) {
            // If the form has fields, we need to register it
            // in the cart context
            if (
                productFormRef.current &&
                hasRegisteredFormRef.current !== productFormIdRef.current
            ) {
                registerProductFormRef.current(productFormRef.current);
                hasRegisteredFormRef.current = productFormIdRef.current;
            }
        }
        return () => {
            if (productFormIdRef.current) {
                // Unregister it upon unmount
                unregisterProductFormRef.current(productFormIdRef.current);
                hasRegisteredFormRef.current = undefined;
            }
        };
    }, [formHasFields]);

    return;
};

export { useRegisterProductFormInCart };
